import { useMutation, useQueryClient, UseQueryOptions, useQuery } from '@tanstack/react-query';
import {
  useDataSources,
  useCreateDataSource,
  useUpdateDataSource,
  useDeleteDataSourceItem,
} from '@/hooks/useDataSources';
import {
  DATA_SOURCE_TYPE_CODES,
  NETWORK_CODES,
  type GroupedDataSource,
  type GroupedDataSourcesPaginatedResponse,
  type CreateDataSourceRequest,
  type UpdateDataSourceRequest,
} from '@/types/dataSources';
import { dataSourcesService } from '@/services/dataSourcesService';

/**
 * Hook to fetch list of influencers using unified datasources API
 * Returns GroupedDataSource[] (datasources with items grouped)
 */
export const useInfluencers = (
  filters?: { account_id?: number; is_active?: boolean; search?: string },
  options?: Omit<UseQueryOptions<GroupedDataSource[]>, 'queryKey' | 'queryFn'>
) => {
  return useQuery<GroupedDataSource[]>({
    queryKey: ['datasources', 'influencers', filters],
    queryFn: async () => {
      const response = await dataSourcesService.getListGrouped({
        account_id: filters?.account_id,
        type: 'influencer',
        is_active: filters?.is_active,
        search: filters?.search,
      });
      return response.data;
    },
    enabled: options?.enabled !== false && !!filters?.account_id,
    staleTime: 1000 * 60 * 5, // 5 minutes
    retry: 1,
    ...options,
  });
};

/**
 * Hook to create a new influencer using unified API
 */
export const useCreateInfluencer = () => {
  const queryClient = useQueryClient();
  const createDataSourceMutation = useCreateDataSource();

  return useMutation({
    mutationFn: async (data: {
      account_id: number;
      author_username?: string;
      author_id?: number;
      is_active?: boolean;
      analysis_since?: string;
    }) => {
      const createRequest: CreateDataSourceRequest = {
        account_id: data.account_id,
        name: data.author_username || 'Influencer',
        data_source_type: DATA_SOURCE_TYPE_CODES.influencer,
        items: [
          {
            author_username: data.author_username,
            author_id: data.author_id,
            network_id: NETWORK_CODES.instagram, // Default to Instagram
            dates: [
              {
                start_date: data.analysis_since || new Date().toISOString().split('T')[0],
                status: data.is_active === false ? 1 : 0,
              },
            ],
          },
        ],
      };

      return await createDataSourceMutation.mutateAsync(createRequest);
    },
    onSuccess: () => {
      // Invalidate only influencers queries (not all datasources)
      queryClient.invalidateQueries({ queryKey: ['datasources', 'influencers'] });
      queryClient.invalidateQueries({ queryKey: ['influencers-discovery'] });
      queryClient.invalidateQueries({ queryKey: ['influencer-filters'] });
    },
    onError: (error) => {
      console.error('[useCreateInfluencer] Mutation error:', error);
    },
  });
};

/**
 * Hook to update an existing influencer using unified API
 */
export const useUpdateInfluencer = () => {
  const queryClient = useQueryClient();
  const updateDataSourceMutation = useUpdateDataSource();

  return useMutation({
    mutationFn: async ({
      id,
      datasource_id,
      data,
    }: {
      id: number;
      datasource_id?: number;
      data: { is_active: boolean; analysis_since?: string; analysis_end?: string };
    }) => {
      if (!datasource_id) {
        throw new Error('datasource_id is required for update');
      }

      // Fetch current datasource once to get existing dates
      const currentDatasource = await dataSourcesService.getDataSourceRaw(datasource_id);
      const currentItem = currentDatasource.items.find(item => item.id === id);

      if (!currentItem) {
        throw new Error('Item not found in datasource');
      }

      const existingDates = currentItem.dates || [];

      // Update dates[0] or create new one if doesn't exist
      const updatedDates: Array<any> = [];

      if (existingDates.length > 0) {
        // Update first date - include id for existing date
        updatedDates.push({
          id: existingDates[0].id,
          start_date: data.analysis_since || existingDates[0].start_date,
          end_date: data.analysis_end !== undefined ? data.analysis_end : existingDates[0].end_date,
          status: existingDates[0].status,
        });

        // Preserve remaining dates with their ids
        for (let i = 1; i < existingDates.length; i++) {
          updatedDates.push({
            id: existingDates[i].id,
            start_date: existingDates[i].start_date,
            end_date: existingDates[i].end_date,
            status: existingDates[i].status,
          });
        }
      } else {
        // Create first date - no id for new date
        updatedDates.push({
          start_date: data.analysis_since || new Date().toISOString().split('T')[0],
          end_date: data.analysis_end || null,
          status: 0,
        });
      }

      const updateRequest: UpdateDataSourceRequest = {
        items: [
          {
            id,
            dates: updatedDates,
          },
        ],
      };

      return await updateDataSourceMutation.mutateAsync({
        datasourceId: datasource_id,
        data: updateRequest,
      });
    },
    onSuccess: () => {
      // Invalidate only influencers queries (not all datasources)
      queryClient.invalidateQueries({ queryKey: ['datasources', 'influencers'] });
      queryClient.invalidateQueries({ queryKey: ['influencers-discovery'] });
      queryClient.invalidateQueries({ queryKey: ['influencer-filters'] });
    },
    onError: (error) => {
      console.error('[useUpdateInfluencer] Mutation error:', error);
    },
  });
};

/**
 * Hook to delete an influencer item (single platform) using unified API
 */
export const useDeleteInfluencer = () => {
  const queryClient = useQueryClient();
  const deleteDataSourceItemMutation = useDeleteDataSourceItem();

  return useMutation({
    mutationFn: async (params: {
      id: number;
      datasource_id: number;
    }) => {
      return await deleteDataSourceItemMutation.mutateAsync({
        datasourceId: params.datasource_id,
        itemId: params.id,
      });
    },
    onSuccess: () => {
      // Invalidate only influencers queries (not all datasources)
      queryClient.invalidateQueries({ queryKey: ['datasources', 'influencers'] });
      queryClient.invalidateQueries({ queryKey: ['influencers-discovery'] });
      queryClient.invalidateQueries({
        queryKey: ['influencer-filters'],
      });
    },
    onError: (error) => {
      console.error('[useDeleteInfluencer] Mutation error:', error);
    },
  });
};

/**
 * Hook to update influencer datasource (for multi-platform editing)
 */
export const useUpdateInfluencerDatasource = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      datasourceId,
      data,
    }: {
      datasourceId: number;
      data: UpdateDataSourceRequest;
    }) => {
      return await dataSourcesService.updateDataSource(datasourceId, data);
    },
    onSuccess: () => {
      // Invalidate only influencers queries (not all datasources)
      queryClient.invalidateQueries({ queryKey: ['datasources', 'influencers'] });
      queryClient.invalidateQueries({ queryKey: ['influencers-discovery'] });
      queryClient.invalidateQueries({
        queryKey: ['influencer-filters'],
      });
    },
    onError: (error) => {
      console.error('[useUpdateInfluencerDatasource] Mutation error:', error);
    },
  });
};

/**
 * Hook to delete entire influencer datasource (all platforms) using unified API
 */
export const useDeleteInfluencerDatasource = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (datasourceId: number) => {
      return await dataSourcesService.deleteDataSource(datasourceId);
    },
    onSuccess: () => {
      // Invalidate only influencers queries (not all datasources)
      queryClient.invalidateQueries({ queryKey: ['datasources', 'influencers'] });
      queryClient.invalidateQueries({ queryKey: ['influencers-discovery'] });
      queryClient.invalidateQueries({
        queryKey: ['influencer-filters'],
      });
    },
    onError: (error) => {
      console.error('[useDeleteInfluencerDatasource] Mutation error:', error);
    },
  });
};
